import apiInstance from "api";
import {
  getUserFailure,
  getUserStart,
  getUserSuccess,
  loginUserFailure,
  loginUserStart,
  loginUserSuccess,
  loginWithEmailFailure,
  loginWithEmailStart,
  loginWithEmailSuccess,
  logoutUserAction,
  overrideSubdomainAction,
  tokenAction,
  twoFactorRequired,
} from "./reducers";
import { clearAllLocationScopes } from "pages/AssetsManagement/AssetsListings/locationScopeSession";
import { clearTeamPromptDismissals } from "lcp/utils/teamPromptDismissal";

const AUTH_ROLE_KEY = "authRoleName";

const readRoleName = (payload) =>
  payload?.userDetails?.userDetails?.role
  ?? payload?.userDetails?.role
  ?? payload?.userDetails?.roleName
  ?? payload?.userDetails?.userProfile?.roleName
  ?? payload?.userDetails?.userProfile?.role
  ?? payload?.roleName
  ?? payload?.role
  ?? null;

const persistRoleName = (payload) => {
  const roleName = readRoleName(payload);
  if (roleName) {
    localStorage.setItem(AUTH_ROLE_KEY, roleName);
  } else {
    localStorage.removeItem(AUTH_ROLE_KEY);
  }
};

// Stores a sign-in envelope ({ accessToken, userDetails }), from the password step
// or from the two-step verification code.
export const applySignIn = (result) => {
  return (dispatch) => {
    if (result?.userDetails?.overrideSubdomain) {
      dispatch(overrideSubdomainAction(result?.userDetails?.overrideSubdomain));
    }

    setTimeout(function () {
      dispatch(loginUserSuccess(result));
      persistRoleName(result);

      if (result?.userDetails?.overrideSubdomain) {
        dispatch(overrideSubdomainAction(result?.userDetails?.overrideSubdomain));
      }

      // Two-step verification happens on the server before it sends a token, so
      // any token that arrives here is a finished sign-in.
      dispatch(tokenAction(result.accessToken));
    }, 500);
  };
};

export const loginUser = (data) => {
  return async (dispatch) => {
    try {
      dispatch(logoutUser());
      dispatch(loginUserStart());
      const response = await apiInstance.post("/auth/login", data);
      const result = response?.data?.data;

      // Two-step verification is on: no session yet, only a challenge for the
      // code step (pages/Login shows it while state.auth.twoFactor is set).
      if (result?.twoFactorRequired) {
        dispatch(twoFactorRequired({ ...result, message: response?.data?.message }));
        return;
      }

      dispatch(applySignIn(result));
    } catch (error) {
      if (!!error?.response?.data) {
        dispatch(loginUserFailure(error.response.data));
      } else {
        dispatch(loginUserFailure(error?.message || "Something went wrong"));
      }
    }
  };
};

export const logoutUser = (data) => {
  return async (dispatch) => {
    try {
      localStorage.removeItem("authToken");
      if (localStorage.getItem("_subdomain")) {
        localStorage.removeItem("_subdomain");
      }
      localStorage.removeItem(AUTH_ROLE_KEY);
      // Drop the super-admin acting-org (stored in sessionStorage) too — otherwise
      // it survives logout and the next login sends X-Acting-Org-Id for a tenant
      // the new subject may not have access to, stalling every request.
      try { sessionStorage.removeItem("abacActingOrgId"); } catch (_) {}
      // The assets list remembers the location scope a user drilled into for
      // the rest of the session; a sign-out has to put the next subject back
      // on their own default scope rather than inheriting this one.
      clearAllLocationScopes();
      // "Don't ask me again" on the inspection-team prompt is a this-session
      // answer. The next subject has declined nothing.
      clearTeamPromptDismissals();
      dispatch(logoutUserAction({}));
    } catch (error) {}
  };
};

export const getUser = (data) => {
  return async (dispatch) => {
    try {
      dispatch(getUserStart());
      // Always send a JSON body. The server's `[FromBody] IntrospectRequest`
      // binding returns 415 Unsupported Media Type when the request has no
      // body — even with Content-Type: application/json on the headers.
      // Callers that have nothing meaningful to send (e.g. the App.js
      // rehydration on mount, or the picker/sidebar refresh) get an empty
      // object; the server reads identity from the JWT claims, not the body.
      const response = await apiInstance.post("/auth/user-info", data ?? {});
      persistRoleName(response.data.data);
      dispatch(getUserSuccess(response.data.data));
    } catch (error) {
      if (!!error?.response?.data) {
        dispatch(getUserFailure(error.response.data));
      } else {
        dispatch(getUserFailure(error?.message || "Something went wrong"));
      }
    }
  };
};

export const sendLoginWithEmailLink = (data) => {
  return async (dispatch) => {
    try {
      dispatch(loginWithEmailStart());
      const response = await apiInstance.post("/auth/login/email", data);
      dispatch(loginWithEmailSuccess(response.data.data));
    } catch (error) {
      if (!!error?.response?.data) {
        dispatch(loginWithEmailFailure(error.response.data));
      } else {
        dispatch(loginWithEmailFailure(error?.message || "Something went wrong"));
      }
    }
  };
};
